import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Grid,
  Alert,
  Button,
} from '@mui/material';
import {
  Home,
  FiberNew,
  BugReport,
  Security,
  Speed,
  Update,
  Search,
  CheckCircle,
  Build,
  DeleteOutline,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';

interface ChangelogEntry {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  features: string[];
  improvements: string[];
  bugFixes: string[];
  security?: string[];
  deprecated?: string[];
  breaking?: string[];
}

const ChangelogPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const changelogData: ChangelogEntry[] = [
    {
      version: '3.4.0',
      date: '2026-01-27',
      type: 'minor',
      features: [
        'WhatsApp Conversation Initiator - Start new conversations with contacts using templates 📱',
        'Contact-to-WhatsApp Quick Start - Initiate WhatsApp conversations directly from contact profiles',
        'Template Variable Mapping UI - Visual interface for mapping contact attributes to template variables',
        'Notifications View All Dialog - Full-screen dialog to view and manage all notifications',
        'WhatsApp Template Configuration Page - Dedicated page for managing WhatsApp Business templates',
        'Template Preview Panel - Real-time preview of templates with variable substitution',
      ],
      improvements: [
        'Enhanced Notifications dropdown with "View All" functionality',
        'Improved notification navigation - clicking notifications now navigates to relevant conversations',
        'Better notification routing to profile/settings for account-related notifications',
        'Extended notifications API to support fetching up to 200 notifications at once',
        'WhatsApp Templates page with tabbed interface (Templates / Sequences)',
        'Automatic WhatsApp channel detection from account inboxes',
        'Contact WhatsApp opt-in status display with last opt-in timestamp',
        'Improved conversation initiator stepper with 4-step wizard flow',
      ],
      bugFixes: [
        'Fixed notification click navigation using correct dashboard routes',
        'Fixed notification type handling with case-insensitive comparisons',
        'Resolved template component structure in ConversationInitiator',
        'Fixed WhatsApp inbox channel type case sensitivity issue',
        'Fixed channel ID resolution using inbox ID instead of whatsapp_channel_id',
      ],
    },
    {
      version: '3.3.0',
      date: '2026-01-19',
      type: 'minor',
      features: [
        'WhatsApp Template System - Complete Implementation 📱',
        'WhatsApp Business API template management with approval workflow (DRAFT → PENDING → APPROVED)',
        'Template Builder UI with category selection (Marketing, Utility, Authentication)',
        'Multi-language template support with variable substitution ({{1}}, {{2}} placeholders)',
        'Template components support: Header (text/image/video/document), Body, Footer, Buttons',
        'Session Management System - 24-hour messaging window tracking',
        'Session state indicators in conversation UI (ACTIVE_24H, TEMPLATE_ONLY, NO_RESPONSE, CLOSED)',
        'Template Sequences - Automated retargeting message flows',
        'Conversation Initiator - Start new WhatsApp conversations with templates',
        'Campaign Integration - WhatsApp template-based marketing campaigns',
        'Rate limiting and cooldown period configuration for campaigns',
        'Background Workers - APScheduler jobs for session monitoring and sequence execution',
        'Template Quick Selector - Autocomplete search in message composer',
        'Template Preview with variable personalization',
      ],
      improvements: [
        'WhatsApp Templates dedicated navigation menu in dashboard sidebar',
        'Enhanced Campaign creation with template selection and variable mapping',
        'Session expiry warnings displayed in conversation interface',
        'Template suggestions based on current session state',
        'Comprehensive API endpoints for template CRUD, submission, and syncing',
        'Improved database schema with proper foreign key relationships',
        'Configuration-driven architecture with template_builder_config table',
        'Contact WhatsApp opt-in tracking and quality rating support',
        'Better error handling in template validation against WhatsApp constraints',
      ],
      bugFixes: [
        'Fixed backend API field mismatch: approval_status → status (enum)',
        'Fixed template language_code → language field mapping',
        'Fixed missing whatsapp_templates router registration in main.py',
        'Fixed template component structure in API responses (header, body, footer, buttons)',
        'Fixed campaign service template status comparison with proper enum',
        'Resolved frontend routing for WhatsApp Templates page',
        'Fixed template approval status tracking in campaign executions',
      ],
    },
    {
      version: '3.2.0',
      date: '2025-12-04',
      type: 'minor',
      features: [
        'Co-Pilot MCP Server Support - SSE/HTTP Transport 🚀',
        'Remote MCP server connectivity over HTTP/HTTPS',
        'Cloud-based MCP services integration',
        'Auto-detection of server type (stdio vs SSE)',
        'Auto AI Handover on Conversation Resolution 🤖',
        'Seamless AI↔Human handoff workflow with auto_resolve flag',
        'Automatic AI re-enabling when conversations are marked as resolved',
        'Auto-Escalation System Enhancements ⚡',
        'Master auto_escalation_enabled toggle for account-level control',
        'Sentiment analysis for customer message evaluation',
        'All 5 escalation triggers now fully operational (confidence, sentiment, repetition, explicit request, max interactions)',
      ],
      improvements: [
        'Enhanced MCP Client Architecture with dual transport support',
        'Improved error handling and logging for SSE connections',
        'Better connection diagnostics for remote servers',
        'Comprehensive configuration validation with helpful error messages',
        'Intelligent AI handover in conversation status toggle endpoint',
        'Escalation resolution now respects auto_resolve flag for conditional AI re-enabling',
        'Auto-creates default escalation rules for new accounts (prevents crashes)',
        'Added interaction count escalation trigger (missing previously)',
        'Comprehensive logging with emoji indicators for easy debugging',
      ],
      bugFixes: [
        'Fixed MCP server configuration validation rejecting SSE servers',
        'Resolved "No such file or directory" error when configuring HTTP/SSE servers',
        'Fixed AI not automatically re-enabling after conversation resolution',
        'Fixed sentiment escalation trigger never firing (was always None)',
        'Fixed interaction count escalation not being checked in should_escalate()',
        'Fixed crashes when escalation rules missing for account',
      ],
    },
    {
      version: '2.0.0',
      date: '2025-11-22',
      type: 'major',
      features: [
        'Support Ticket System - Submit and track support tickets directly from the platform',
        'Demo Booking - Schedule product demo calls with sales team',
        'API Documentation - Comprehensive API docs with code examples and SDKs',
        'Help Center - Interactive help documentation with 8 major sections',
        'Changelog Page - Version tracking and release notes',
      ],
      improvements: [
        'Enhanced Reports section with 8 advanced analytics dashboards',
        'Improved email notifications with professional HTML templates',
        'Better error handling in frontend forms',
        'Updated contact information in Help & Support tab',
      ],
      bugFixes: [
        'Fixed compilation errors in ConversationQualityScoring component',
        'Fixed orphaned code in SLAComplianceTracking component',
        'Resolved state variable issues in SettingsPage',
        'Fixed enum type mismatches in support ticket model',
      ],
    },
    {
      version: '1.3.0',
      date: '2025-11-18',
      type: 'minor',
      features: [
        'CSAT (Customer Satisfaction) Analytics Dashboard',
        'Advanced sentiment analysis for customer feedback',
        'CSAT response tracking and reporting',
        'Rating distribution visualization',
      ],
      improvements: [
        'Enhanced widget integration with CSAT surveys',
        'Improved response time tracking',
        'Better data visualization for customer satisfaction metrics',
      ],
      bugFixes: [
        'Fixed CSAT survey submission errors',
        'Resolved widget loading issues',
        'Fixed rating calculation accuracy',
      ],
    },
    {
      version: '1.2.0',
      date: '2025-11-17',
      type: 'minor',
      features: [
        'User Access Management & RBAC (Role-Based Access Control)',
        'Custom role creation with granular permissions',
        'Team-based access control',
        'Activity logging and audit trails',
      ],
      improvements: [
        'Enhanced security with permission-based UI rendering',
        'Improved user management interface',
        'Better team collaboration features',
      ],
      bugFixes: [
        'Fixed permission inheritance issues',
        'Resolved role assignment conflicts',
        'Fixed audit log timestamp accuracy',
      ],
      security: [
        'Implemented row-level security for data access',
        'Added permission validation middleware',
        'Enhanced JWT token security',
      ],
    },
    {
      version: '1.1.0',
      date: '2025-11-07',
      type: 'minor',
      features: [
        'Email Template System with dynamic variables',
        'Rich text editor for email composition',
        'Template library with pre-built templates',
        'Email preview before sending',
      ],
      improvements: [
        'Improved email delivery reliability',
        'Better template organization',
        'Enhanced variable substitution engine',
      ],
      bugFixes: [
        'Fixed email formatting issues',
        'Resolved template rendering errors',
        'Fixed attachment handling',
      ],
    },
    {
      version: '1.0.0',
      date: '2025-11-03',
      type: 'major',
      features: [
        'AI-Native Platform Launch',
        'Multi-channel support (Website, WhatsApp, Instagram, Email, SMS)',
        'Real-time conversation management',
        'AI-powered auto-responses',
        'AI Co-Pilot for agent assistance',
        'Conversation summaries and sentiment analysis',
        'Intent detection and classification',
        'Knowledge Base integration',
        'Automation rules and workflows',
        'Team management and assignment',
        'Contact management with custom attributes',
        'Canned responses library',
        'Labels and categorization',
        'Business hours configuration',
        'Basic reporting and analytics',
      ],
      improvements: [
        'High-performance WebSocket connections',
        'Responsive UI design for all devices',
        'Fast message delivery (<100ms)',
        'Scalable architecture for enterprise use',
      ],
      bugFixes: [],
      breaking: [
        'Initial release - no backward compatibility',
      ],
    },
    {
      version: '0.9.0',
      date: '2025-10-20',
      type: 'minor',
      features: [
        'Knowledge Base Management',
        'Article creation and editing',
        'Category organization',
        'Search functionality',
      ],
      improvements: [
        'Improved search algorithms',
        'Better content organization',
      ],
      bugFixes: [
        'Fixed article formatting issues',
        'Resolved search indexing problems',
      ],
    },
    {
      version: '0.8.0',
      date: '2025-10-16',
      type: 'minor',
      features: [
        'AI-enabled conversation toggle',
        'Selective AI activation per conversation',
        'AI usage analytics',
      ],
      improvements: [
        'Better AI response quality',
        'Faster AI processing',
      ],
      bugFixes: [
        'Fixed AI toggle persistence',
        'Resolved conversation state sync issues',
      ],
    },
  ];

  const filteredChangelog = changelogData.filter((entry) =>
    entry.version.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.features.some(f => f.toLowerCase().includes(searchTerm.toLowerCase())) ||
    entry.improvements.some(i => i.toLowerCase().includes(searchTerm.toLowerCase())) ||
    entry.bugFixes.some(b => b.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getVersionColor = (type: 'major' | 'minor' | 'patch') => {
    switch (type) {
      case 'major':
        return '#f44336';
      case 'minor':
        return '#2196f3';
      case 'patch':
        return '#4caf50';
      default:
        return '#757575';
    }
  };

  const getVersionLabel = (type: 'major' | 'minor' | 'patch') => {
    switch (type) {
      case 'major':
        return 'MAJOR RELEASE';
      case 'minor':
        return 'MINOR RELEASE';
      case 'patch':
        return 'PATCH';
      default:
        return 'RELEASE';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard/settings')}
          sx={{ textTransform: 'none' }}
        >
          Back to Settings
        </Button>
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          color="inherit"
          onClick={() => navigate('/dashboard')}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MuiLink>
        <Typography color="text.primary">Changelog</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          <Update sx={{ fontSize: 40, mr: 1, verticalAlign: 'middle' }} />
          Changelog
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Stay updated with all the new features, improvements, and bug fixes
        </Typography>
      </Box>

      {/* Current Version Banner */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Current Version: v3.4.0
            </Typography>
            <Typography variant="body2">
              Released on January 27, 2026 - WhatsApp Conversation Initiator & Enhanced Notifications
            </Typography>
          </Box>
          <Button variant="contained" endIcon={<ArrowForward />}>
            View Release
          </Button>
        </Box>
      </Alert>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search changelog (version, features, fixes...)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 2, mb: 4, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Legend
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberNew sx={{ color: '#2196f3' }} />
              <Typography variant="body2">New Features</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Speed sx={{ color: '#4caf50' }} />
              <Typography variant="body2">Improvements</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BugReport sx={{ color: '#ff9800' }} />
              <Typography variant="body2">Bug Fixes</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security sx={{ color: '#f44336' }} />
              <Typography variant="body2">Security</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Changelog Entries */}
      {filteredChangelog.length === 0 ? (
        <Alert severity="info">
          No changelog entries match your search. Try different keywords.
        </Alert>
      ) : (
        filteredChangelog.map((entry, index) => (
          <Card key={index} sx={{ mb: 3 }}>
            <CardContent>
              {/* Version Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                  v{entry.version}
                </Typography>
                <Chip
                  label={getVersionLabel(entry.type)}
                  sx={{
                    bgcolor: getVersionColor(entry.type),
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {new Date(entry.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Features */}
              {entry.features.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiberNew sx={{ color: '#2196f3' }} />
                    New Features
                  </Typography>
                  <List dense>
                    {entry.features.map((feature, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle fontSize="small" sx={{ color: '#2196f3' }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Improvements */}
              {entry.improvements.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Speed sx={{ color: '#4caf50' }} />
                    Improvements
                  </Typography>
                  <List dense>
                    {entry.improvements.map((improvement, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle fontSize="small" sx={{ color: '#4caf50' }} />
                        </ListItemIcon>
                        <ListItemText primary={improvement} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Bug Fixes */}
              {entry.bugFixes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugReport sx={{ color: '#ff9800' }} />
                    Bug Fixes
                  </Typography>
                  <List dense>
                    {entry.bugFixes.map((fix, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle fontSize="small" sx={{ color: '#ff9800' }} />
                        </ListItemIcon>
                        <ListItemText primary={fix} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Security */}
              {entry.security && entry.security.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Security sx={{ color: '#f44336' }} />
                    Security
                  </Typography>
                  <List dense>
                    {entry.security.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle fontSize="small" sx={{ color: '#f44336' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Deprecated */}
              {entry.deprecated && entry.deprecated.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteOutline sx={{ color: '#757575' }} />
                    Deprecated
                  </Typography>
                  <List dense>
                    {entry.deprecated.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <DeleteOutline fontSize="small" sx={{ color: '#757575' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Breaking Changes */}
              {entry.breaking && entry.breaking.length > 0 && (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      ⚠️ Breaking Changes
                    </Typography>
                    <List dense>
                      {entry.breaking.map((item, idx) => (
                        <ListItem key={idx} sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Build fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Footer */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: '#e3f2fd', textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Stay Informed
        </Typography>
        <Typography variant="body1" paragraph>
          Subscribe to our newsletter to get notified about new releases and updates
        </Typography>
        <Button variant="contained" color="primary" size="large">
          Subscribe to Updates
        </Button>
      </Paper>

      {/* Version Roadmap */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          📅 Upcoming Features (Roadmap)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
              v3.4.5 (Next) - Foundation Fixes
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Slack Integration - Full Implementation" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Pre-Chat Forms - Enhanced Fields & Routing" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Business Hours - Auto-Responder & Scheduler" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Notifications - Real-time WebSocket" />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
              v3.5.0 (Planned)
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="WhatsApp Catalog & Product Messages" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Advanced Campaign Analytics Dashboard" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="A/B Testing for Message Templates" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Bulk Contact Import with WhatsApp Opt-in" />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
              v4.0.0 (Future) - Voice & Video Calling
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="WebRTC Voice & Video Calling" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="AI Voice Agent - Automated voice responses" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="AI Video Avatar - AI-powered video responses" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Call Recording & Transcription" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Human-AI Handover for Voice/Video" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <FiberNew />
                </ListItemIcon>
                <ListItemText primary="Mobile apps (iOS & Android)" />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ChangelogPage;
